import {
  PetrotecGroupLogo,
  PetrotecLogo,
  PetroassistLogo,
  LinkedinIcon,
  FacebookIcon,
  InstagramIcon,
  YoutubeIcon,
} from "../atoms/Icons";
import Grid from "../atoms/Grid";
import { ButtonElement } from "../atoms/Buttons";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import getImageFromStrapi from "../../utils/api/getImageFromStrapi";
import { ExternalLinkSimpleIcon } from "../atoms/Icons";
import { prefix } from "../../utils/utils";
function transformFooterMenu(footerData) {
  return footerData.data.attributes.menu.map((menuItem) => ({
    title: menuItem.label,
    children: menuItem.submenu.map((subMenuItem) => ({
      title: subMenuItem.label,
      url: subMenuItem.link,
      target: subMenuItem.new_tab ? "_blank" : "_self",
    })),
  }));
}

export default function Footer({ info, websiteType, translations }) {
  const router = useRouter();
  const items = info ? transformFooterMenu(info) : [];
  const extra_info = info?.data?.attributes;
  const partnerships = extra_info?.partnership ? extra_info.partnership : [];
  const date = new Date();
  const year = date.getFullYear();
  const baseUrl = process.env.NEXT_PUBLIC_STRAPI_CLIENT || "";
  const privacyPolicyPath =
    translations?.privacy_policy_url || "/privacy-policy";
  const termsAndConditionsPath =
    translations?.terms_and_conditions_url || "/terms-and-conditions";
  const localePrefix =
    router?.locale &&
    router?.defaultLocale &&
    router.locale !== router.defaultLocale
      ? `/${router.locale}`
      : "";
  const normalizePath = (path) => (path?.startsWith("/") ? path : `/${path}`);
  const hasLocalePrefix = (path) => {
    const locales = router?.locales || [];
    return locales.some(
      (locale) =>
        path === `/${locale}` || path.startsWith(`/${locale}/`),
    );
  };
  const joinUrl = (base, path) => {
    const normalized = normalizePath(String(path || ""));
    const localized =
      localePrefix && !hasLocalePrefix(normalized)
        ? `${localePrefix}${normalized}`
        : normalized;
    return `${base.replace(/\/+$/, "")}/${localized.replace(/^\/+/, "")}`;
  };
  const privacyPolicyHref = joinUrl(baseUrl, privacyPolicyPath);
  const termsAndConditionsHref = joinUrl(baseUrl, termsAndConditionsPath);

  return (
    <footer className="container-custom bg-gray-100 border-t border-gray-300 overflow-hidden">
      <div className="flex flex-col">
        {/* Top */}
        <div className="pt-20 pb-40 1024:py-40">
          <Grid extraClass="!gap-y-40 !1024:gap-y-40">
            <div className="col-span-4 640:col-span-6 1024:col-span-2">
              {websiteType === "petrotec-group" ? (
                <PetrotecGroupLogo />
              ) : websiteType === "petroassist" ? (
                <PetroassistLogo />
              ) : (
                <PetrotecLogo />
              )}
            </div>
            {items &&
              items.length > 0 &&
              items.map((menu, key) => (
                <div key={key} className="col-span-2 flex flex-col gap-y-10">
                  {menu.title && (
                    <span className="text-xs font-medium leading-125">
                      {menu.title}
                    </span>
                  )}
                  <ul className="flex flex-col gap-y-10">
                    {menu.children &&
                      menu.children.length > 0 &&
                      menu.children.map((item, key) =>
                        item.target == "_blank" ? (
                          <li key={key}>
                            <ButtonElement
                              label={item.title}
                              variant="link"
                              type="external"
                              externallink={item.url}
                              isEditor={false}
                              newTab={true}
                              extraClass="gap-x-6 !font-regular"
                              extraClassIcon="!w-16 !h-16 mt-2"
                            />
                          </li>
                        ) : (
                          <li key={key}>
                            <ButtonElement
                              label={item.title}
                              variant="link"
                              type=""
                              externallink={item.url}
                              newTab={false}
                              isEditor={false}
                              extraClass="!font-regular"
                            />
                          </li>
                        ),
                      )}
                  </ul>
                </div>
              ))}
          </Grid>
        </div>

        {/* Bottom */}
        <div className="flex flex-col w-full border-t border-solid border-gray-300 pt-20 pb-10">
          <span className="text-gray-900 text-xxs mb-12">
            {translations?.support}
          </span>
          <div className="flex flex-col 640:flex-row justify-between gap-y-24 pb-20">
            <div className="flex flex-row gap-x-32">
              {partnerships.length > 0 &&
                partnerships.map((partnership, key) =>
                  partnership?.link ? (
                    <Link
                      key={key}
                      href={prefix(partnership.link)}
                      as={prefix(partnership.link)}
                      target={partnership.new_tab ? "_blank" : "_self"}
                      rel={
                        partnership.new_tab ? "noopener noreferrer" : undefined
                      }
                      aria-label={`${partnership.logo.data.attributes.alternativeText || "Partner"} ${partnership.new_tab ? "(opens in new tab)" : ""}`}
                    >
                      <Image
                        src={getImageFromStrapi(
                          partnership.logo.data.attributes.url,
                        )}
                        alt=""
                        className="h-32 w-auto object-contain"
                        width={200}
                        height={32}
                      />
                    </Link>
                  ) : (
                    <Image
                      src={getImageFromStrapi(
                        partnership.logo.data.attributes.url,
                      )}
                      alt={
                        partnership.logo.data.attributes.alternativeText ||
                        `Partner Logo ${key + 1}`
                      }
                      className="h-32 w-auto object-contain"
                      width={200}
                      height={32}
                      key={key}
                    />
                  ),
                )}
            </div>
            <div className="flex flex-row gap-x-12">
              {extra_info?.linked_in_url ? (
                <Link
                  href={
                    extra_info.linked_in_url ? extra_info.linked_in_url : "#"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Linkedin"
                >
                  <LinkedinIcon />
                </Link>
              ) : (
                <></>
              )}
              {extra_info?.facebook_url ? (
                <Link
                  href={extra_info.facebook_url ? extra_info.facebook_url : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                >
                  <FacebookIcon />
                </Link>
              ) : (
                <></>
              )}
              {extra_info?.instagram_url ? (
                <Link
                  href={
                    extra_info.instagram_url ? extra_info.instagram_url : "#"
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                >
                  <InstagramIcon />
                </Link>
              ) : (
                <></>
              )}
              {extra_info?.youtube_url ? (
                <Link
                  href={extra_info.youtube_url ? extra_info.youtube_url : "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Youtube"
                >
                  <YoutubeIcon />
                </Link>
              ) : (
                <></>
              )}
            </div>
          </div>
          {/* Copy and policy */}
          <div className="flex flex-col 640:flex-row 640:justify-between gap-y-6">
            <span className="text-xxxs tracking-05">
              {translations?.copyright} © {year} Petrotec Group -{" "}
              {translations?.all_rights_reserved}
            </span>
            <ul className="flex flex-col 640:flex-row gap-x-32 gap-y-16 640:gap-y-6">
              {websiteType == "petroassist" ? (
                <li>
                  <a
                    target="_blank"
                    href="/petroassist-compete-2030.pdf"
                    className="btn-link group text-xxxs"
                  >
                    <span className="py-10 underline decoration-transparent media-hover:group-hover:decoration-gray-900 underline-offset-4 duration-300">
                      Compete 2030 Petroassist
                    </span>
                  </a>
                </li>
              ) : (
                <></>
              )}
              {websiteType == "petrotec" ? (
                <li>
                  <a
                    target="_blank"
                    href="/petrotec-compete-2030.pdf"
                    className="btn-link group text-xxxs"
                  >
                    <span className="py-10 underline decoration-transparent media-hover:group-hover:decoration-gray-900 underline-offset-4 duration-300">
                      Compete 2030 Petrotec
                    </span>
                  </a>
                </li>
              ) : (
                <></>
              )}
              <li>
                <Link
                  href={prefix(privacyPolicyHref)}
                  as={prefix(privacyPolicyHref)}
                  className="btn-link group text-xxxs"
                  aria-label={translations?.personal_data_protection}
                >
                  <span className="py-10 underline decoration-transparent media-hover:group-hover:decoration-gray-900 underline-offset-4 duration-300">
                    {translations?.personal_data_protection}
                  </span>
                </Link>
              </li>
              <li>
                <Link
                  href={prefix(termsAndConditionsHref)}
                  as={prefix(termsAndConditionsHref)}
                  className="btn-link group text-xxxs"
                  aria-label={translations?.terms_and_conditions_of_use}
                >
                  <span className="py-10 underline decoration-transparent media-hover:group-hover:decoration-gray-900 underline-offset-4 duration-300">
                    {translations?.terms_and_conditions_of_use}
                  </span>
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
